import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Alert,
  Link, // Added
} from "@mui/material";
import React from "react";
import useApiEp from "../../hooks/useApiEp";
import { useParams } from "react-router-dom";
import BackBtn from "../../widgets/BackBtn";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

const COLUMNS = [
  { key: "customerId", label: "Customer ID" },
  { key: "phoneNumberId", label: "Phone Number ID" },
  { key: "wabaAccountId", label: "WABA Account ID" },
  { key: "appId", label: "App ID" },
  { key: "partner", label: "Partner" },
  { key: "subscriptionPlan", label: "Subscription Plan" },
  { key: "wallet", label: "Wallet" },
];

const SubAccountsList = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error } = useApiEp(
    `/root/get-sub-accounts/${id}`
  );

  return (
    <Container sx={{ mt: 3 }}>
      <BackBtn />

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, mt: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          Sub Accounts of
        </Typography>
        <Chip label={id} color="primary" variant="outlined" size="medium" />
        {!isLoading && data && (
          <Chip
            icon={<PeopleAltOutlinedIcon />}
            label={`${data.length} account${data.length !== 1 ? "s" : ""}`}
            color="success"
            size="small"
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      {/* Error */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || "Failed to load sub accounts."}
        </Alert>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ fontWeight: 700, bgcolor: "grey.100", width: 48 }}
              >
                #
              </TableCell>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{ fontWeight: 700, bgcolor: "grey.100" }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Loading skeletons */}
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: COLUMNS.length + 1 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {/* Empty state */}
            {!isLoading && data?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length + 1}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <Typography color="text.secondary">
                    No sub accounts found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Data rows */}
            {!isLoading &&
              data?.map((row, index) => (
                <TableRow
                  key={row.customerId ?? index}
                  hover
                  sx={{ "&:last-child td": { border: 0 } }}
                >
                  <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                    {index + 1}
                  </TableCell>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.key}>
                      {col.key === "customerId" ? (
                        <Link
                          href={`/home/${row.customerId}/account-details`}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 500 }}
                        >
                          {row.customerId ?? "—"}
                        </Link>
                      ) : col.key === "subscriptionPlan" ? (
                        <Chip
                          label={row[col.key] ?? "—"}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : col.key === "wallet" ? (
                        <Typography variant="body2" fontWeight={500}>
                          {row[col.key] != null ? `₹ ${row[col.key]}` : "—"}
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          {row[col.key] ?? "—"}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default SubAccountsList;
